import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Lead, Order, AuditRequest } from '../types';

interface Stats {
  totalLeads: number;
  totalOrders: number;
  totalRevenue: number;
  pendingAudits: number;
}

export default function AdminDashboard() {
  const { user, userData } = useAuth();
  const [stats, setStats] = useState<Stats>({
    totalLeads: 0,
    totalOrders: 0,
    totalRevenue: 0,
    pendingAudits: 0,
  });
  const [recentLeads, setRecentLeads] = useState<Lead[]>([]);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [recentAudits, setRecentAudits] = useState<AuditRequest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadDashboardData() {
      if (!user || !userData?.isAdmin) {
        setLoading(false);
        return;
      }

      try {
        // Load stats
        const [leadsSnap, ordersSnap, auditsSnap] = await Promise.all([
          getDocs(collection(db, 'leads')),
          getDocs(collection(db, 'orders')),
          getDocs(query(collection(db, 'audit_requests'), where('status', '==', 'pending'))),
        ]);

        const orders = ordersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Order[];
        const totalRevenue = orders
          .filter(o => o.status === 'paid')
          .reduce((sum, o) => sum + o.amount, 0);

        setStats({
          totalLeads: leadsSnap.size,
          totalOrders: ordersSnap.size,
          totalRevenue,
          pendingAudits: auditsSnap.size,
        });

        // Load recent leads
        const leadsQuery = query(
          collection(db, 'leads'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const leadsData = await getDocs(leadsQuery);
        setRecentLeads(
          leadsData.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Lead[]
        );

        // Load recent orders
        const ordersQuery = query(
          collection(db, 'orders'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const ordersData = await getDocs(ordersQuery);
        setRecentOrders(
          ordersData.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Order[]
        );

        // Load recent audit requests
        const auditsQuery = query(
          collection(db, 'audit_requests'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const auditsData = await getDocs(auditsQuery);
        setRecentAudits(
          auditsData.docs.map(doc => ({ id: doc.id, ...doc.data() })) as AuditRequest[]
        );
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
      } finally {
        setLoading(false);
      }
    }

    loadDashboardData();
  }, [user, userData?.isAdmin]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-secondary">Loading dashboard...</div>
      </div>
    );
  }

  if (!user || !userData?.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Access Denied</h2>
          <p className="text-secondary">You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-7xl px-4">
        <h1 className="text-3xl font-bold text-primary mb-8">Admin Dashboard</h1>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-sm text-secondary mb-1">Total Leads</div>
            <div className="text-3xl font-bold text-primary">{stats.totalLeads}</div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-sm text-secondary mb-1">Total Orders</div>
            <div className="text-3xl font-bold text-primary">{stats.totalOrders}</div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-sm text-secondary mb-1">Total Revenue</div>
            <div className="text-3xl font-bold text-primary">
              R{stats.totalRevenue.toLocaleString()}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-sm text-secondary mb-1">Pending Audits</div>
            <div className="text-3xl font-bold text-primary">{stats.pendingAudits}</div>
          </div>
        </div>

        {/* Recent Leads */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Recent Leads</h2>
          {recentLeads.length === 0 ? (
            <p className="text-secondary">No leads yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Email</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Source</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Date</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentLeads.map(lead => (
                    <tr key={lead.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-3 text-sm">{lead.email}</td>
                      <td className="py-3 px-3 text-sm text-secondary">
                        {lead.utm?.source || lead.source}
                      </td>
                      <td className="py-3 px-3 text-sm text-secondary">
                        {lead.createdAt.toDate().toLocaleDateString()}
                      </td>
                      <td className="py-3 px-3 text-sm">
                        <span className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                          {lead.consent ? 'Consented' : 'No consent'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Orders */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Recent Orders</h2>
          {recentOrders.length === 0 ? (
            <p className="text-secondary">No orders yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Order ID</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Email</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Amount</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Date</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map(order => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-3 text-sm font-mono">{order.id.slice(0, 8)}</td>
                      <td className="py-3 px-3 text-sm">{order.email}</td>
                      <td className="py-3 px-3 text-sm font-semibold">
                        R{order.amount.toLocaleString()}
                      </td>
                      <td className="py-3 px-3 text-sm text-secondary">
                        {order.createdAt.toDate().toLocaleDateString()}
                      </td>
                      <td className="py-3 px-3 text-sm">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs ${
                            order.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : order.status === 'refunded'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {order.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Audit Requests */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Recent Audit Requests</h2>
          {recentAudits.length === 0 ? (
            <p className="text-secondary">No audit requests yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Company</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Deal Type</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Tier</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Price</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Date</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-secondary">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentAudits.map(audit => (
                    <tr key={audit.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-3 text-sm font-medium">{audit.company}</td>
                      <td className="py-3 px-3 text-sm text-secondary">{audit.dealType}</td>
                      <td className="py-3 px-3 text-sm">
                        <span className="capitalize">{audit.tier}</span>
                      </td>
                      <td className="py-3 px-3 text-sm font-semibold">
                        R{audit.priceZAR.toLocaleString()}
                      </td>
                      <td className="py-3 px-3 text-sm text-secondary">
                        {audit.createdAt.toDate().toLocaleDateString()}
                      </td>
                      <td className="py-3 px-3 text-sm">
                        <span
                          className={`inline-block px-2 py-1 rounded text-xs ${
                            audit.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : audit.status === 'in_progress'
                              ? 'bg-blue-100 text-blue-800'
                              : audit.status === 'pending_payment'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {audit.status.replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
